/**
 * Article master: parts, oils and other goods with their part numbers, cross
 * numbers and (optionally) stock. Prices in minor units.
 */
import { DomainError } from "./errors";
import { clean, plateSearchKey } from "./masterData";
import type { PriceMode } from "./workOrder";
import type { Row } from "./legacyImport";

export type ArticleNumberKind = "oe" | "aftermarket" | "ean" | "other";

export interface ArticleNumber {
  number: string;
  kind: ArticleNumberKind;
  brand?: string | null;
}

export interface ArticleInput {
  partNumber?: string | null;
  name: string;
  brand?: string | null;
  category?: string | null;
  unit?: string | null;
  purchasePrice?: number | null;
  salePrice?: number | null;
  salePriceMode?: PriceMode;
  vatRate?: number;
  trackStock?: boolean;
  bulk?: boolean;
  minStock?: number | null;
  active?: boolean;
  notes?: string | null;
}

/** Search key of a part number: letters and digits only, upper case (as the database column part_search). */
export const partSearchKey = plateSearchKey;

export function validateArticle(a: ArticleInput): ArticleInput {
  const name = clean(a.name);
  if (!name) throw new DomainError("ARTICLE_NAME_REQUIRED");
  for (const p of [a.purchasePrice, a.salePrice]) {
    if (p !== null && p !== undefined && (!Number.isInteger(p) || p < 0)) throw new DomainError("INVALID_PRICE", String(p));
  }
  if (a.minStock !== null && a.minStock !== undefined && (!Number.isFinite(a.minStock) || a.minStock < 0)) {
    throw new DomainError("INVALID_STOCK_QUANTITY", String(a.minStock));
  }
  return {
    ...a,
    name,
    partNumber: clean(a.partNumber)?.toUpperCase() ?? null,
    brand: clean(a.brand),
    category: clean(a.category),
    unit: clean(a.unit) ?? (a.bulk ? "l" : "db"),
    // Oil from a barrel warns at 10 litres unless set otherwise.
    minStock: a.minStock ?? (a.bulk ? 10 : null),
  };
}

/**
 * Suggested sale price from the purchase price and the workshop's margin, as a
 * gross price rounded to whole units of the currency.
 */
export function suggestedSalePrice(purchaseNet: number, markupPercent: number, vatRate: number, minorPerUnit = 1): number {
  const gross = purchaseNet * (1 + markupPercent / 100) * (1 + vatRate / 100);
  return Math.round(gross / minorPerUnit) * minorPerUnit;
}

export type StockStatus = "none" | "ok" | "low" | "out";

/** How the stock stands: only warns, never blocks. */
export function stockStatus(a: { trackStock?: boolean; stock?: number; minStock?: number | null }, needed = 0): StockStatus {
  if (!a.trackStock) return "none";
  const left = (a.stock ?? 0) - needed;
  if (left < 0 || (a.stock ?? 0) <= 0) return "out";
  if (a.minStock !== null && a.minStock !== undefined && left < a.minStock) return "low";
  return "ok";
}

// ---------------------------------------------------------------------------
// Previous program's article master (cikktorzs.csv)
// ---------------------------------------------------------------------------
export interface LegacyArticle {
  legacyId: string;
  partNumber: string | null;
  name: string;
  category: string | null;
  unit: string;
  salePrice: number | null;
  vatRate: number;
  usageCount: number;
  lastUsed: string | null;
}

export interface LegacyArticlePlan {
  articles: LegacyArticle[];
  /** Rows whose "part number" was free text (kept as a name only). */
  freeText: number;
  skipped: number;
}

const num = (v: string | undefined) => {
  const n = Number(String(v ?? "").trim().replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

/**
 * The old program let free text into the part number field ("csapágy",
 * "motul multi dctf"). A part number has a digit and is not the name itself;
 * otherwise the row becomes an article with a name only.
 */
export function looksLikePartNumber(partNumber: string, name: string): boolean {
  const p = partNumber.trim();
  return /\d/.test(p) && p.toLowerCase() !== name.trim().toLowerCase();
}

export function planLegacyArticles(rows: readonly Row[]): LegacyArticlePlan {
  const out: LegacyArticle[] = [];
  let freeText = 0;
  let skipped = 0;
  for (const r of rows) {
    const legacyId = (r.id ?? "").trim().replace(/\.0$/, "");
    const raw = (r.cikkszam ?? "").trim();
    const name = (r.megnevezes ?? "").trim() || raw;
    if (!legacyId || !name) {
      skipped++;
      continue;
    }
    const isNumber = raw !== "" && looksLikePartNumber(raw, name);
    if (!isNumber) freeText++;
    const net = num(r.utolso_netto_egysegar);
    const gross = num(r.utolso_brutto_egysegar);
    const vatRate = net > 0 && gross > 0 && Math.abs(gross / net - 1) < 0.005 ? 0 : 27;
    out.push({
      legacyId,
      partNumber: isNumber ? raw.toUpperCase() : null,
      name,
      category: clean(r.kategoria),
      // cans of oil (4 l, 1 l) were sold by the piece
      unit: "db",
      salePrice: Number.isFinite(gross) && gross > 0 ? Math.round(gross) : null,
      vatRate,
      usageCount: Number.isFinite(num(r.hasznalat_db)) ? Math.round(num(r.hasznalat_db)) : 0,
      lastUsed: clean(r.utolso_hasznalat),
    });
  }
  return { articles: out, freeText, skipped };
}
